/**
 * 챗봇 위젯 IIFE 번들 진입점
 *
 * 외부 사이트에서 다음과 같이 사용:
 *
 *   <script src="/chatbot/widget.js" defer></script>
 *   <script>
 *     window.MaillinkChatbot.init({
 *       apiBaseUrl: '',
 *       autoOpen: false,
 *     });
 *   </script>
 */
import { createRoot, type Root } from 'react-dom/client';
import App from './App';
import './styles/globals.css';

interface ChatbotConfig {
  /** API 호출 시 prefix할 base URL. 같은 도메인이면 빈 문자열 */
  apiBaseUrl?: string;
  /** 페이지 로드 시 자동으로 챗봇 열기 */
  autoOpen?: boolean;
  /** 운영자 디버그 패널 표시 (내부 검수용) */
  debug?: boolean;
}

interface ChatbotInstance {
  init: (config?: ChatbotConfig) => void;
  open: () => void;
  close: () => void;
  destroy: () => void;
}

let root: Root | null = null;
let mountElement: HTMLElement | null = null;

const MaillinkChatbot: ChatbotInstance = {
  init(config: ChatbotConfig = {}) {
    if (root) {
      console.warn('[MaillinkChatbot] Already initialized');
      return;
    }

    // 마운트 element 생성
    mountElement = document.createElement('div');
    mountElement.id = 'maillink-chatbot-root';
    document.body.appendChild(mountElement);

    // 설정을 window에 저장 (App에서 참조)
    (window as unknown as { __MAILLINK_CHATBOT_CONFIG__: ChatbotConfig }).__MAILLINK_CHATBOT_CONFIG__ =
      config;

    root = createRoot(mountElement);
    root.render(<App />);
  },

  open() {
    // chatStore를 통해 위젯 열기 (구현은 chatStore에서)
    window.dispatchEvent(new CustomEvent('maillink-chatbot:open'));
  },

  close() {
    window.dispatchEvent(new CustomEvent('maillink-chatbot:close'));
  },

  destroy() {
    if (root) {
      root.unmount();
      root = null;
    }
    if (mountElement) {
      mountElement.remove();
      mountElement = null;
    }
  },
};

// 글로벌 노출
(window as unknown as { MaillinkChatbot: ChatbotInstance }).MaillinkChatbot = MaillinkChatbot;

export default MaillinkChatbot;
